// src/features/groups/components/GroupEdit.tsx 
import React, { useState } from 'react';
import {
  Edit,
  SimpleForm,
  TextInput,
  required,
  useTranslate,
  useNotify,
  useRedirect,
  useRecordContext,
  LoadingIndicator,
} from 'react-admin';
import {
  Box,
  Grid,
} from '@mui/material';
import { 
  Edit as EditIcon,
} from '@mui/icons-material';
import { MyToolbar } from '../../../components/forms/MyToolbar';
import { MyFormContainer } from '../../../components/forms/MyFormContainer';
import { 
  CenteredFormLayout,
  FormCard, 
  MyFormSection 
} from '../../../components/forms/MyFormLayout';
import BaseDeleteDialog from '../../../components/common/BaseDeleteDialog';

import { useGroupValidation } from '../hooks/useGroupValidation';
import { Group } from '../types';

const GroupEditTitle: React.FC = () => {
  const record = useRecordContext<Group>();
  const translate = useTranslate();
  
  if (!record) return <LoadingIndicator />;
  
  return (
    <span>
      {translate('resources.groups.titles.edit', 'Editar Grupo')}: {record.name}
    </span>
  );
};

export const GroupEdit: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const record = useRecordContext<Group>();
  const recordId = record?.id;
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { validateGroupName, validateDescription } = useGroupValidation();

  const transform = (data: any) => ({
    name: data.name?.trim(),
    description: data.description?.trim() || '',
  });

  const handleSuccess = () => {
    notify(translate('resources.groups.notifications.updated', 'Grupo actualizado con éxito'), { type: 'success' });
    redirect('list', 'groups');
  };
  
  const handleError = (error: any) => {
    console.error('Error updating group:', error);
    notify(error.message || translate('resources.groups.errors.update', 'Error al actualizar el grupo'), { type: 'error' });
  };
  
  const handleCancel = () => redirect('list', 'groups');
  const handleDeleteClick = () => setShowDeleteDialog(true);
  const handleDeleteClose = () => setShowDeleteDialog(false);

  const deleteMessage = record 
    ? translate('resources.groups.dialogs.delete_message', { name: record.name })
    : translate('resources.groups.dialogs.delete_generic');

  return (
    <Edit
      title={<GroupEditTitle />}
      transform={transform} 
      mutationOptions={{ 
        onSuccess: handleSuccess, 
        onError: handleError 
      }}
      redirect="list"
      mutationMode="pessimistic"
    >    
      <MyFormContainer maxWidth="lg">
        <SimpleForm
          toolbar={
            <MyToolbar 
              onCancel={handleCancel} 
              resource="groups" 
              showDelete 
              onDelete={handleDeleteClick}
            />
          }
        >
         <CenteredFormLayout>
            
              <MyFormSection
                title={translate('resources.groups.sections.basic_info', 'Información Básica')}
                subtitle={translate('resources.groups.sections.basic_info_subtitle', 'Datos básicos y configuración del grupo')}
                icon={<EditIcon />}
              >
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: 3
                }}>
                  <TextInput
                    source="name"
                    label={translate('resources.groups.fields.name', 'Nombre del Grupo')}
                    validate={[required(), validateGroupName]}
                    fullWidth
                    variant="outlined"
                    helperText={translate(
                      'resources.groups.help.name',
                      'Nombre único que identifica al grupo'
                    )}
                  />

                  <TextInput
                    source="description"
                    label={translate('resources.groups.fields.description', 'Descripción')}
                    validate={validateDescription}
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    helperText={translate(
                      'resources.groups.help.description',
                      'Descripción opcional del grupo (máx. 500 caracteres)'
                    )}
                  />
                </Box>
              </MyFormSection>
           
          </CenteredFormLayout>
        </SimpleForm>
      </MyFormContainer>

      {showDeleteDialog && (
        <BaseDeleteDialog
          open={showDeleteDialog}
          onClose={handleDeleteClose}
          resource="groups"
          recordId={recordId}
          title={translate('resources.groups.dialogs.delete_title', 'Eliminar Grupo')}
          message={deleteMessage}
          warningMessage={
            translate(
              'resources.groups.dialogs.delete_warning',
              'Esta acción no se puede deshacer.'
            )
          }
        />
      )}
    </Edit>
  );
};

export default GroupEdit;